import React from 'react';
import { connect } from 'react-redux';
import { setLocale } from '../../actions/intl';
import COPY from './copy.json';
import {
  t, PHONE, PHONE_LABEL, initReveal,
  IconPhone, IconTick, IconStore, IconPlay,
  Header, Footer, CallBar
} from './SiteChrome';

/*
 * Halaman /app — tujuan tombol "Download our app" di header, dan halaman
 * pendaratan kampanye iklan pemasangan aplikasi.
 *
 * Sengaja BOLEH diindeks: beranda menyasar layanannya ("car recovery UAE"),
 * halaman ini menyasar aplikasinya ("car recovery app", "tow truck app Dubai"),
 * dan hanya halaman ini yang membawa schema MobileApplication. Dua halaman, dua
 * niat pencarian, tidak saling memakan. Kalau nanti dialihkan untuk promo
 * sesaat, tambahkan noindex — halaman yang hidup untuk satu kampanye tidak
 * pantas ikut bersaing di pencarian organik.
 */

export const IOS_URL = 'https://apps.apple.com/ae/app/basrecovery/id6744970584';
export const AND_URL = 'https://play.google.com/store/apps/details?id=com.nagmalshamalportal.basrecovery';

const FAQ = [1, 2, 3, 4, 5];

/*
 * SENGAJA TANPA aggregateRating. Markup rating harus mencerminkan ulasan nyata
 * yang bisa diverifikasi; mengarangnya melanggar aturan data terstruktur Google
 * dan bisa berujung tindakan manual. Tambahkan hanya kalau rating toko sudah
 * ada, dan angkanya harus sama persis dengan yang di toko.
 */
const LD_APP = {
  '@context': 'https://schema.org',
  '@type': 'MobileApplication',
  '@id': 'https://basrecovery.com/app#app',
  name: 'Bas Recovery',
  applicationCategory: 'TravelApplication',
  operatingSystem: 'iOS 13.0 or later, Android 8.0 or later',
  description: 'Book 24/7 car recovery and roadside assistance across the UAE. Fixed price before dispatch, live driver tracking, and payment by Apple Pay, Google Pay, card or cash.',
  url: 'https://basrecovery.com/app',
  installUrl: [IOS_URL, AND_URL],
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'AED' },
  inLanguage: ['en', 'ar'],
  publisher: { '@type': 'Organization', name: 'Bas Recovery', '@id': 'https://basrecovery.com/#business' },
  featureList: [
    'Book recovery, battery, tyre, fuel and lockout assistance',
    'Fixed price shown before you confirm',
    'Live driver tracking',
    'Apple Pay, Google Pay, card or cash',
    'English and Arabic'
  ]
};

// Schema selalu en-US: satu URL = satu bahasa untuk crawler, sama seperti
// halaman lain. {phone} disulih supaya jawabannya tidak memuat placeholder.
const LD_FAQ = {
  '@context': 'https://schema.org', '@type': 'FAQPage',
  mainEntity: FAQ.map(n => ({
    '@type': 'Question', name: COPY['en-US'][`appp.faq.${n}q`],
    acceptedAnswer: {
      '@type': 'Answer',
      text: COPY['en-US'][`appp.faq.${n}a`].replace('{phone}', '050 505 2213')
    }
  }))
};

const StoreButtons = ({ locale }) => {
  const tx = k => t(locale, k);
  return (
    <div className="stores">
      {/* data-conversion menandai tombol untuk GA4 / Google Ads. Belum ada yang
          membacanya — penanda dulu, pemasangan event menyusul, supaya markup
          tidak perlu disentuh lagi saat itu. */}
      <a className="store" href={IOS_URL} data-conversion="install_click_ios"
        aria-label={tx('appp.iosAria')} rel="noopener">
        <IconStore /><span><small>{tx('appp.ios.small')}</small>{tx('appp.ios.big')}</span>
      </a>
      <a className="store" href={AND_URL} data-conversion="install_click_android"
        aria-label={tx('appp.andAria')} rel="noopener">
        <IconPlay /><span><small>{tx('appp.and.small')}</small>{tx('appp.and.big')}</span>
      </a>
    </div>
  );
};

class AppPage extends React.Component {
  state = { navOpen: false };

  toggleNav = () => this.setState(s => ({ navOpen: !s.navOpen }));
  closeNav = () => this.setState({ navOpen: false });

  switchLang = () => {
    const next = this.props.currentLocale === 'ar' ? 'en-US' : 'ar';
    document.documentElement.lang = next;
    this.props.setLocale({ locale: next });
  };

  componentDidMount() {
    this.onKey = e => { if (e.key === 'Escape') this.closeNav(); };
    document.addEventListener('keydown', this.onKey);
    this.io = initReveal();
  }
  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKey);
    if (this.io) this.io.disconnect();
  }

  render() {
    const { navOpen } = this.state;
    const L = this.props.currentLocale || 'en-US';
    const tx = (k, v) => t(L, k, v);

    return (
      <div>
        <a className="skip" href="#main">{tx('nav.skip')}</a>

        <Header
          locale={L} current="/app"
          navOpen={navOpen}
          onToggleNav={this.toggleNav}
          onCloseNav={this.closeNav}
          onSwitchLang={this.switchLang}
        />

        <main id="main">
          {/* ── HERO ── */}
          <section className="hero">
            <div className="wrap hero-grid">
              <div>
                <p className="eyebrow">{tx('appp.eyebrow')}</p>
                <h1>{tx('appp.h1a')} <em>{tx('appp.h1b')}</em></h1>
                <p className="lead">{tx('appp.lead')}</p>
                <StoreButtons locale={L} />
                <p className="hero-note">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s7-3.2 7-9V6l-7-3-7 3v6c0 5.8 7 9 7 9Z" /></svg>
                  {tx('appp.note')}
                </p>
              </div>

              {/* Ilustrasi vektor, bukan tangkapan layar: tetap tajam di layar
                  kerapatan apa pun dan hanya beberapa KB. Ganti dengan tangkapan
                  layar asli begitu tampilan app final. */}
              <div className="phone-fig" role="img" aria-label={tx('appp.phoneAria')}>
                <svg viewBox="0 0 300 600">
                  <rect x="14" y="8" width="272" height="584" rx="40" fill="#1E1E1E" stroke="#3A3A3A" strokeWidth="2" />
                  <rect x="26" y="20" width="248" height="560" rx="32" fill="#131313" />
                  <rect x="120" y="28" width="60" height="7" rx="3.5" fill="#2A2A2A" />
                  <rect x="26" y="46" width="248" height="300" fill="#191919" />
                  <path d="M26 130 L274 112 M26 250 L274 268 M96 46 L112 346 M206 46 L192 346"
                    stroke="#262626" strokeWidth="9" fill="none" />
                  <path d="M104 150 C 132 182, 120 232, 150 262 S 168 300, 164 320"
                    stroke="#FF8800" strokeWidth="4" fill="none" strokeLinecap="round" strokeDasharray="9 7" />
                  <circle cx="164" cy="322" r="9" fill="#FF8800" />
                  <circle cx="164" cy="322" r="17" fill="#FF8800" opacity=".22" />
                  <g transform="translate(88,134)">
                    <circle r="15" fill="#FF8800" />
                    <path d="M-8 3V-3h5l2-2h3l1 2h2v3h-1.4a1.6 1.6 0 0 0-3.2 0H-3a1.6 1.6 0 0 0-3.2 0Z" fill="#131313" />
                  </g>
                  <rect x="26" y="330" width="248" height="250" rx="26" fill="#1E1E1E" />
                  <rect x="132" y="342" width="36" height="4" rx="2" fill="#3A3A3A" />
                  <text x="46" y="382" fill="#FFA033" fontFamily="Montserrat,sans-serif" fontSize="11" fontWeight="700">ARRIVING</text>
                  <text x="46" y="418" fill="#F5F5F5" fontFamily="Montserrat,sans-serif" fontSize="34" fontWeight="800">12</text>
                  <text x="82" y="418" fill="#9E9E9E" fontFamily="Montserrat,sans-serif" fontSize="13" fontWeight="600">min</text>
                  <text x="46" y="442" fill="#C4C4C4" fontFamily="Montserrat,sans-serif" fontSize="10">Flatbed · 3.2 km</text>
                  <rect x="46" y="458" width="208" height="1" fill="#333" />
                  <rect x="46" y="524" width="208" height="42" rx="12" fill="#FF8800" />
                  <text x="150" y="551" fill="#131313" fontFamily="Montserrat,sans-serif" fontSize="14" fontWeight="800" textAnchor="middle">Confirm</text>
                </svg>
              </div>
            </div>
          </section>

          {/* ── TRUST ── */}
          <section className="trust" style={{ paddingBlock: 0 }}>
            <div className="wrap trust-grid">
              {[1, 2, 3, 4].map(n => (
                <div className="trust-item" key={n}>
                  <div className="trust-num">{tx(`appp.trust.${n}n`)}</div>
                  <div className="trust-lbl">{tx(`appp.trust.${n}`)}</div>
                </div>
              ))}
            </div>
          </section>

          {/* ── APA YANG DILAKUKAN APP ── */}
          <section className="band-warm">
            <div className="wrap">
              <div className="sec-head">
                <p className="eyebrow">{tx('appp.does.eyebrow')}</p>
                <h2>{tx('appp.does.h2')}</h2>
              </div>
              <div className="two-up two-up-gap">
                {[['before', tx('appp.before.h3')], ['wait', tx('appp.wait.h3')]].map(([key, head]) => (
                  <div key={key}>
                    <h3>{head}</h3>
                    <div className="ticks">
                      {[1, 2, 3].map(n => (
                        <div className="tick" key={n}>
                          <IconTick />
                          <span><strong>{tx(`appp.${key}.${n}b`)}</strong>{tx(`appp.${key}.${n}`)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── CARA KERJA ── */}
          <section>
            <div className="wrap">
              <div className="sec-head">
                <p className="eyebrow">{tx('appp.how.eyebrow')}</p>
                <h2>{tx('appp.how.h2')}</h2>
              </div>
              <div className="steps">
                {[1, 2, 3].map(n => (
                  <div className="step" key={n}>
                    <h3>{tx(`appp.how.${n}t`)}</h3>
                    <p>{tx(`appp.how.${n}`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── PEMBAYARAN ── */}
          <section className="band-sand">
            <div className="wrap">
              <div className="sec-head">
                <p className="eyebrow">{tx('appp.pay.eyebrow')}</p>
                <h2>{tx('appp.pay.h2')}</h2>
                <p className="lead">{tx('appp.pay.lead')}</p>
              </div>
              <div className="ticks">
                {[1, 2, 3].map(n => (
                  <div className="tick" key={n}>
                    <IconTick />
                    <span><strong>{tx(`appp.pay.${n}b`)}</strong>{tx(`appp.pay.${n}`)}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── FAQ ── */}
          <section className="band-warm">
            <div className="wrap wrap-narrow">
              <div className="sec-head">
                <p className="eyebrow">{tx('appp.faq.eyebrow')}</p>
                <h2>{tx('appp.faq.h2')}</h2>
              </div>
              <div className="faq">
                {FAQ.map(n => (
                  <details key={n} open={n === 1}>
                    <summary>{tx(`appp.faq.${n}q`)}</summary>
                    <div>
                      <p>
                        {tx(`appp.faq.${n}a`, { phone: PHONE_LABEL })}
                        {n === 3 && <> <a href="/coverage">{tx('appp.faq.3link')}</a>.</>}
                        {n === 5 && <> <a href="/providers">{tx('appp.faq.5link')}</a>.</>}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* ── CTA PENUTUP ──
              Ajakan pasang yang kedua. Siapa pun yang membaca sampai sini tanpa
              menekan tombol di hero butuh tombolnya lagi, bukan disuruh menggulir
              balik — dan nomor telepon ada di bawahnya sebagai jalan jujur untuk
              orang yang sedang mogok dan tidak akan memasang apa pun dulu. */}
          <section className="band-night">
            <div className="wrap" style={{ textAlign: 'center' }}>
              <p className="eyebrow">{tx('appp.cta.eyebrow')}</p>
              <h2 style={{ maxWidth: '26ch', marginInline: 'auto' }}>{tx('appp.cta.h2')}</h2>
              <p style={{ maxWidth: '52ch', margin: 'var(--sp-4) auto var(--sp-6)' }}>{tx('appp.cta.lead')}</p>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <StoreButtons locale={L} />
              </div>
              <p className="hero-note" style={{ marginTop: 'var(--sp-6)', justifyContent: 'center' }}>
                <IconPhone />
                {tx('appp.cta.now')}{' '}
                <a href={`tel:${PHONE}`} style={{ color: 'var(--orange-hot)', textDecoration: 'underline' }}>
                  {tx('appp.cta.call', { phone: PHONE_LABEL })}
                </a>
              </p>
            </div>
          </section>
        </main>

        <Footer locale={L} />
        <CallBar locale={L} />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LD_APP) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LD_FAQ) }} />
      </div>
    );
  }
}

const mapState = state => ({ currentLocale: state && state.intl && state.intl.locale });
const mapDispatch = { setLocale };

export default connect(mapState, mapDispatch)(AppPage);
